import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { isDefined, ConfigInstanceToken, ConfigInterface } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, of, Subject } from 'rxjs';
import { catchError, filter, takeUntil, tap } from 'rxjs/operators';
import { EquipmentModel } from '../interfaces/equipment.interface';
import { InterimModel } from '../interfaces/interim.interface';
import { NotificationService } from '../services/notification/notification.service';
import { navigateToForms, showInterimsLimitModal } from '../store/actions/protocols.action';
import { equipments, interims, isEquipmentsLoading } from '../store/selectors/protocols.selector';
import { AppState } from '../store/state/state';
import { isInterimExistInEquipments } from '../utilities/equipments.utilities';

@Component({
    selector: 'pace-fi-equipments-list',
    templateUrl: './equipments-list.component.html',
    styleUrls: ['./equipments-list.component.scss'],
    standalone: false,
})
export class EquipmentsListComponent implements OnInit, OnDestroy {
    public equipmentsList$: Observable<EquipmentModel[]>;
    public isLoading$: Observable<boolean>;
    private destroyTrigger$: Subject<void>;
    private interims: InterimModel[] = [];
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly store: Store<AppState>,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly notificationService: NotificationService,
        public readonly modalInstance: BsModalRef,
    ) {
        this.destroyTrigger$ = new Subject<void>();
        this.equipmentsList$ = store.select(equipments).pipe(filter(isDefined));
        this.isLoading$ = store.select(isEquipmentsLoading);
    }

    public ngOnInit(): void {
        this.store
            .select(interims)
            .pipe(
                tap((interimsList) => {
                    this.log.debug(
                        `EquipmentsListComponent: ngOnInit: received interims length is ${
                            interimsList.length
                        } and interims are:`,
                        interimsList,
                    );
                    this.interims = interimsList;
                }),
                catchError((error) => {
                    this.log.error(
                        `EquipmentsListComponent: ngOnInit: error while getting interims ${JSON.stringify(
                            error,
                            Object.getOwnPropertyNames(error),
                        )}`,
                    );
                    return of(undefined);
                }),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe();
    }

    public openForms(equipment: EquipmentModel): void {
        this.modalInstance.hide();
        const maxInterimsLimit = this.configService.getMaxInterimsCount();
        this.log.debug(
            `EquipmentsListComponent: openForms: maxInterimsLimit received from config service '${maxInterimsLimit}'`,
        );
        // show interims limit reached popup if opened interims >= interims limit
        if (
            this.interims.length >= maxInterimsLimit &&
            !isInterimExistInEquipments(this.interims, [{ ...equipment }])
        ) {
            this.store.dispatch(showInterimsLimitModal({ interims: this.interims }));
        } else {
            this.notificationService.setEquipmentForNotification(equipment);
            this.store.dispatch(navigateToForms({ orderNumber: equipment.OrderNumber }));
        }
    }

    public ngOnDestroy(): void {
        this.destroyTrigger$.next();
    }
}
